import axios from 'axios';
import { API_URL } from '../utils/apiConfig';

const api = axios.create({
  baseURL: API_URL,
});

// Attach auth token from localStorage to every request
api.interceptors.request.use((config) => {
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      if (user.token) {
        config.headers.Authorization = `Bearer ${user.token}`;
      }
    } catch (err) {
      console.error('Error parsing user from storage', err);
    }
  }
  return config;
});

// Resolve a Stripe product env var with DEV/PROD switching
function resolveStripeProductEnv(baseName) {
  const env = process.env.REACT_APP_ENV || 'development'; // [[memory:5095605]]
  const suffix = env === 'production' ? 'PROD' : 'DEV';
  const withSuffix = process.env[`${baseName}_${suffix}`];
  return withSuffix || process.env[baseName];
}

// All users now use the same product - REACT_APP_STRIPE_150 (no custom pricing calculation)

export const billingService = {
  async createCheckoutSession({ priceId, productId, quantity = 1, promoCode, billingInterval = 'yearly' }) {
    // For ALL users, ALWAYS use the standard product ID - no custom pricing calculations
    if (!productId && !priceId) {
      if (billingInterval === 'monthly') {
        // Use monthly product ID
        const monthlyPriceId = resolveStripeProductEnv('REACT_APP_STRIPE_MONTHLY_PRICE');
        if (monthlyPriceId) {
          priceId = monthlyPriceId;
          console.log('✅ Using configured monthly price ID');
        } else {
          productId = resolveStripeProductEnv('REACT_APP_STRIPE_MONTHLY');
          console.log('✅ Using configured monthly product ID');
        }
      } else {
        // Use yearly product ID (default)
        const standardPriceId = resolveStripeProductEnv('REACT_APP_STRIPE_150_PRICE');
        if (standardPriceId) {
          priceId = standardPriceId;
          console.log('✅ Using configured $150/year price ID');
        } else {
          productId = resolveStripeProductEnv('REACT_APP_STRIPE_150');
          console.log('✅ Using configured product ID (will use existing $150/year price)');
        }
      }
    }

    const { data } = await api.post('/billing/create-checkout-session', {
      priceId,
      productId,
      quantity,
      promoCode,
      billingInterval,
    });
    return data;
  },

  async createBillingPortalSession() {
    const { data } = await api.post('/billing/create-billing-portal-session');
    return data;
  },

  async getSubscription() {
    const { data } = await api.get('/billing/subscription');
    return data; // { subscription, role, adminEmail }
  },

  async updateSeatCount(seatCount, newMemberEmails=[]) {
    console.log('🔄 [BILLING_SERVICE] Updating seat count:', { seatCount, newMemberEmails });
    try {
      const { data } = await api.post('/billing/update-seat-count', { seatCount, newMemberEmails });
      console.log('✅ [BILLING_SERVICE] Seat count update response:', data);
      return data;
    } catch (error) {
      console.error('❌ [BILLING_SERVICE] Seat count update failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  async getCustomPricing(quantity = 1) {
    const { data } = await api.get(`/billing/custom-pricing?quantity=${quantity}`);
    return data; // { pricingTier, pricePerSeat, totalAmount, quantity, discountPercent, etc. }
  },

  async updateUserPricing(userId, pricingData) {
    const { data } = await api.put(`/billing/user-pricing/${userId}`, pricingData);
    return data;
  },

  async checkPromoAvailability() {
    try {
      const { data } = await api.get('/promo/check');
      return data;
    } catch (error) {
      console.error('Error checking promo availability:', error);
      return { available: false };
    }
  },
}; 